import { u as scrollAnimationsDisabled } from "@theme/utils";
import { t as gsapWithCSS } from "@theme/gsap";
import { SplitText } from "@theme/SplitText";
import { t as CustomEase } from "@theme/CustomEase";
//#region frontend/islands/hero-animation.js
gsapWithCSS.registerPlugin(SplitText, CustomEase);
CustomEase.create("bowl-in", "M0,0 C0,0.95 0.35,1 1,1");
var HeroAnimation = class extends window.HTMLElement {
	constructor() {
		super();
		this._animationComplete = false;
		this._currentBreakpoint = this._getBreakpoint();
		this._onResize = () => {
			clearTimeout(this._resizeTimer);
			this._resizeTimer = setTimeout(() => {
				this._currentBreakpoint = this._getBreakpoint();
				this.calculateBowlSize();
			}, 100);
		};
	}
	async connectedCallback() {
		this.ctaGroup = this.querySelector(".hero__cta-group");
		this.ctaButtons = this.querySelectorAll(".hero__cta-button");
		this.checklist = this.querySelector(".hero__checklist");
		this.marqueeOverlay = this.querySelector(".hero__marquee-overlay");
		this.marqueeBar = this.marqueeOverlay.previousElementSibling;
		this.stars = this.querySelector(".hero__stars");
		this.reviews = this.querySelector(".hero__reviews");
		this.bannerText = this.querySelector(".hero__banner-text");
		this.headline = this.bannerText.querySelector("h1");
		this.subheadline = this.bannerText.querySelector("p");
		this.starChildren = this.stars.children;
		await document.fonts.ready;
		await new Promise((resolve) => requestAnimationFrame(resolve));
		this._resizeObserver = new window.ResizeObserver(this._onResize);
		this._resizeObserver.observe(this.closest("section"));
		this._resizeObserver.observe(this.bannerText.parentElement);
		if (scrollAnimationsDisabled()) {
			this._animationComplete = true;
			this.calculateBowlSize();
			gsapWithCSS.set(this.marqueeOverlay, {
				rotation: 0,
				autoAlpha: this._bowlHidden ? 0 : 1,
				force3D: true
			});
			window.addEventListener("resize", this._onResize);
			this.bindButtonShimmer();
			return;
		}
		this.splitReviews = SplitText.create(this.reviews, {
			type: "words",
			aria: false
		});
		this.splitHeadline = SplitText.create(this.headline, {
			type: "lines",
			aria: false
		});
		gsapWithCSS.set([
			this.stars.children,
			this.splitReviews.words,
			this.splitHeadline.lines,
			this.ctaGroup,
			this.subheadline,
			this.checklist
		].filter(Boolean), {
			opacity: 0,
			y: 5,
			visibility: "visible"
		});
		this.calculateBowlSize();
		gsapWithCSS.set(this.marqueeOverlay, {
			autoAlpha: 0,
			y: Math.min(window.innerHeight, 900),
			rotation: -95,
			force3D: true
		});
		const animatedEls = [
			this.stars,
			this.reviews,
			this.headline,
			this.subheadline,
			this.ctaGroup,
			this.checklist,
			this.marqueeOverlay
		].filter(Boolean);
		gsapWithCSS.set(animatedEls, { willChange: "transform, opacity" });
		this.tl = gsapWithCSS.timeline({
			defaults: {
				ease: "power3.out",
				duration: .5,
				yoyo: true
			},
			onComplete: () => {
				this._animationComplete = true;
				this.calculateBowlSize();
				gsapWithCSS.set(animatedEls, { willChange: "auto" });
				if (this.ctaButtons[0]) this.generateShimmer(this.ctaButtons[0], 1500, .4);
			}
		});
		gsapWithCSS.set([
			this.headline,
			this.reviews,
			this.stars
		], {
			opacity: 1,
			visibility: "visible"
		});
		this.tl.to(this.starChildren, {
			opacity: 1,
			y: 0,
			stagger: { amount: .25 },
			force3D: true
		}, "<0.15");
		this.tl.to(this.splitReviews.words, {
			opacity: 1,
			y: 0,
			stagger: { amount: .25 },
			force3D: true
		}, "<");
		this.tl.to(this.splitHeadline.lines, {
			opacity: 1,
			y: 0,
			duration: 1.5,
			stagger: { each: .3 },
			force3D: true
		}, "<0.5");
		this.tl.to(this.subheadline, {
			opacity: 1,
			y: 0,
			duration: 1.25,
			force3D: true
		}, ">-1.05");
		if (this.ctaGroup) this.tl.to(this.ctaGroup, {
			opacity: 1,
			y: 0,
			duration: .75,
			force3D: true
		}, "<");
		if (this.checklist) this.tl.to(this.checklist, {
			opacity: 1,
			y: 0,
			duration: .75,
			force3D: true
		}, "<0.15");
		this.tl.set(this.marqueeOverlay, { autoAlpha: () => this._bowlHidden ? 0 : 1 }, "<-0.1");
		this.tl.to(this.marqueeOverlay, {
			y: () => this._targetY,
			rotation: 0,
			duration: 1,
			ease: "bowl-in",
			force3D: true
		}, "<0.5");
		window.addEventListener("resize", this._onResize);
		this.bindButtonShimmer();
	}
	bindButtonShimmer() {
		this.ctaButtons.forEach((button) => {
			button.addEventListener("mouseenter", (e) => {
				this._shimmerTimer = setTimeout(() => this.generateShimmer(e.target, 800, .2), 200);
			});
			button.addEventListener("mouseleave", () => this.killShimmer());
			button.addEventListener("focus", (e) => this.generateShimmer(e.target, 800, .2));
		});
	}
	killShimmer() {
		clearTimeout(this._shimmerTimer);
	}
	generateShimmer(target, duration, opacity) {
		const shimmer = document.createElement("span");
		shimmer.classList.add("shimmer");
		shimmer.style.animationDuration = `${duration}ms`;
		shimmer.style.opacity = opacity;
		target.appendChild(shimmer);
		window.setTimeout(() => {
			shimmer.remove();
		}, duration + 200);
	}
	_getBreakpoint() {
		const w = window.innerWidth;
		if (w >= 1280) return "xl";
		if (w >= 768) return "md";
		return "sm";
	}
	calculateBowlSize() {
		const size = this._currentBreakpoint === "xl" ? 650 : this._currentBreakpoint === "md" ? 550 : 350;
		const section = this.closest("section");
		const gap = 32;
		const content = this.bannerText.parentElement;
		const contentBottom = content.getBoundingClientRect().bottom - parseFloat(window.getComputedStyle(content).paddingBottom);
		const totalSpace = Math.floor(section.getBoundingClientRect().bottom - contentBottom);
		const marqueeHeight = this._currentBreakpoint === "xl" ? 0 : this.marqueeBar.offsetHeight;
		this._targetY = size - Math.min(size / 2 + marqueeHeight, totalSpace - gap);
		this._bowlHidden = size - this._targetY - marqueeHeight < size / 4;
		gsapWithCSS.set(this.marqueeOverlay, {
			width: size,
			height: size,
			transformOrigin: "center center"
		});
		if (this._animationComplete) gsapWithCSS.set(this.marqueeOverlay, {
			y: this._targetY,
			autoAlpha: this._bowlHidden ? 0 : 1
		});
	}
	disconnectedCallback() {
		clearTimeout(this._resizeTimer);
		this._resizeObserver?.disconnect();
		window.removeEventListener("resize", this._onResize);
		this.tl?.kill();
		this.splitReviews?.revert();
		this.splitHeadline?.revert();
	}
};
window.customElements.define("hero-animation", HeroAnimation);
//#endregion
